import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    PriceRequest,
    PriceRequestLine,
    PriceRequestLineInput,
    PriceRequestLineOutcome,
    PriceValues,
} from 'models/price-request';

/** One line of the lab's response grid as typed: number fields stay strings until sent. */
export type ResponseLineState = {
    outcome: PriceRequestLineOutcome;
    price: string;
    tiotandvardPrice: string;
    deliveryTime: string;
};

export type PriceField = 'price' | 'tiotandvardPrice' | 'deliveryTime';

const REQUIRED_FIELDS: PriceField[] = ['price', 'deliveryTime'];

const toText = (value: number | null | undefined): string =>
    value === null || value === undefined ? '' : String(value);

const toNumber = (value: string): number | null => {
    if (value.trim() === '') return null;
    const parsed = Number(value.replace(',', '.'));
    return Number.isNaN(parsed) ? null : parsed;
};

const fromLine = (line: PriceRequestLine): ResponseLineState => {
    // A line answered CHANGED shows its proposal; otherwise "change" starts from today's values.
    const values: PriceValues = line.outcome === PriceRequestLineOutcome.CHANGED && line.proposed ? line.proposed : {};
    return {
        outcome: line.outcome,
        price: toText(values.price),
        tiotandvardPrice: toText(values.tiotandvardPrice),
        deliveryTime: toText(values.deliveryTime),
    };
};

const initialState = (lines: PriceRequestLine[]): Record<string, ResponseLineState> =>
    Object.fromEntries(lines.map((line) => [line.action, fromLine(line)]));

/**
 * DD-3697: the lab's unsaved answers per line. Resets whenever the request's saved lines
 * change (a save, a reload, another user's edit — the backend bumps `revision`). Validation is
 * the backend's (savePriceRequestLines); this only shapes the input.
 */
export const usePriceRequestResponse = (request: PriceRequest) => {
    const [lines, setLines] = useState(() => initialState(request.lines));
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        setLines(initialState(request.lines));
        setIsDirty(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- revision changes with every line write (DD-3697 contract)
    }, [request._id, request.revision, request.status]);

    const setOutcome = useCallback(
        (line: PriceRequestLine, outcome: PriceRequestLineOutcome) => {
            setIsDirty(true);
            setLines((previous) => {
                const current = previous[line.action];
                const isFirstChange = outcome === PriceRequestLineOutcome.CHANGED && !current.price;
                return {
                    ...previous,
                    [line.action]: isFirstChange
                        ? {
                              outcome,
                              price: toText(line.current.price),
                              tiotandvardPrice: toText(line.current.tiotandvardPrice),
                              deliveryTime: toText(line.current.deliveryTime),
                          }
                        : { ...current, outcome },
                };
            });
        },
        [setLines],
    );

    const setValue = useCallback(
        (action: string, field: PriceField, value: string) => {
            setIsDirty(true);
            setLines((previous) => ({ ...previous, [action]: { ...previous[action], [field]: value } }));
        },
        [setLines],
    );

    const toInput = useCallback(
        (): PriceRequestLineInput[] =>
            request.lines.map((line) => {
                const state = lines[line.action] ?? fromLine(line);
                return {
                    action: line.action,
                    outcome: state.outcome,
                    proposed:
                        state.outcome === PriceRequestLineOutcome.CHANGED
                            ? {
                                  price: toNumber(state.price),
                                  tiotandvardPrice: toNumber(state.tiotandvardPrice),
                                  deliveryTime: toNumber(state.deliveryTime),
                              }
                            : undefined,
                };
            }),
        [lines, request.lines],
    );

    // A changed line must leave the product with a price and a delivery time; a field left
    // empty keeps today's value, so it is only missing when there is none today (a withdrawn
    // product whose values the Labs page blanked). The backend refuses such an answer too.
    const isMissing = useCallback(
        (line: PriceRequestLine, field: PriceField) => {
            const state = lines[line.action];
            return (
                state?.outcome === PriceRequestLineOutcome.CHANGED &&
                REQUIRED_FIELDS.includes(field) &&
                state[field].trim() === '' &&
                (line.current[field] === null || line.current[field] === undefined)
            );
        },
        [lines],
    );

    const missingLines = useMemo(
        () => request.lines.filter((line) => REQUIRED_FIELDS.some((field) => isMissing(line, field))),
        [isMissing, request.lines],
    );

    const counts = useMemo(() => {
        const states = Object.values(lines);
        return {
            changed: states.filter((state) => state.outcome === PriceRequestLineOutcome.CHANGED).length,
            unchanged: states.filter((state) => state.outcome === PriceRequestLineOutcome.UNCHANGED).length,
            withdrawn: states.filter((state) => state.outcome === PriceRequestLineOutcome.WITHDRAWN).length,
        };
    }, [lines]);

    return { lines, isDirty, setOutcome, setValue, toInput, counts, toNumber, isMissing, missingLines };
};

export type PriceRequestResponse = ReturnType<typeof usePriceRequestResponse>;
